import React, { CSSProperties, Fragment, useMemo, useState } from 'react';
import _sortBy from 'lodash/sortBy';
import cx from 'classnames';

import type {
  WProjectCustomizationElementView,
  WProjectCustomizationFullView,
  WProjectCustomizationPageView,
  WProjectFullView,
} from '@zola/svc-web-api-ts-client';

import { ConvertWebApiDeclaredToActual } from '@zola-helpers/client/dist/es/@types/svc-web-api';

import { COLORS3 } from '../../../../styles/emotion';
import { stringToHexCode } from '../../../../util/color/hexColor';
import { RefreshLinkIcon } from '../../../../components/SvgIcons/RefreshLink';
import { ButtonV3 } from '../../../../components/ButtonV3/ButtonV3';
import { hasBoundingBoxAdjustedTag } from '../../util/hasBoundingBoxAdjustedTag';
import { useGetPreviewStyling } from '../../hooks/useGetPreviewStyling';
import { CARD_SHAPE_SVG_PATHS, isCardShapeWithClipPath } from '../../constants/cardShapes';
import { CARD_TYPE_MAP, isEnvelope } from '../../constants/cardTypes';
import { isCardTypeWithAdditionalShapes } from '../../constants/cardTypeFeatures/cardTypesWithAdditionalShapes';
import { CanvasPreview } from './CanvasPreview';
import { CustomizationElement } from '../ElementViews/CustomizationElement';
import { AnimatedCanvas } from '../AnimatedCanvas/AnimatedCanvas';
import { findLeadCustomization, getSelectedColor } from '../../util/customization';
import { placeCardFilter } from '../../util/placeCardFilter';
import { getCardDimensionsInPoints } from '../../util/getCardDimensionsInPoints';

import { ptToPx } from '../../../../util/font/pointPixelConversion';
import styles from './digitalCanvas.module.less';
import { getCardBleed } from '../../util/getCardBleed';

type DigitalCanvasPage = {
  width: number;
  height: number;
  folded: boolean;
  orientation?: WProjectCustomizationPageView.OrientationEnum | ('LANDSCAPE' | 'PORTRAIT');
  tags?: string[];
  bleed_left?: number;
  bleed_right?: number;
  bleed_top?: number;
  bleed_bottom?: number;
};

// Types
export type DigitalCanvasProps = {
  stylesCanvas?: string;

  page: DigitalCanvasPage;
  elements: WProjectCustomizationElementView[];
  backgroundColor?: string;
  opacity?: number;
  strongShadow?: boolean;
  foilColor?: string;
  aspectRatioValue?: string;
  cardBackgroundColor?: string;
  hideFoldedDisplay?: boolean;
  shape?: string;
  cardType?: string;
  clipPathId?: string;
  disableBleed?: boolean;
  isMobile?: boolean;
  alignItems?: string;
  justifyContent?: CSSProperties['justifyContent'];
} & (
  | {
      width: number;
      imageWidth: number;
      windowWidth?: number;
      isXsMobile?: boolean;

      disableTranslationScale?: false;
    }
  | {
      width: number;

      /**
       * The Digital Canvas has used some very funky math and positioning to get the
       * image scaled to the "right" size.  This is a flag added to "use simpler math"
       * for scaling and positioning the image.
       *
       * - The page (from the backend, not the web-page) specifies how big the card is (in points - say 504pt wide by 360pt tall).
       *   This includes X pts of bleed which must be subtracted sometimes
       * - We convert the card size from points to pixels, accounting for bleed. e.g. ((504 - 18pt) * 4 / 3) = 672px
       * - The DigitalCanvas component takes a prop, imageWidth (eg 218px), in px, for how big we should show the image on the screen
       * - We compute a scale of imageWidth / cardSizePx (eg 218px / 672px = 0.324)
       * - We use the style prop on a div to set the width to the requested image width (218px)
       * - Within the canvas component, we render in points and use a transform: scale( 0.324 ) to shrink everything to the right size.
       *
       * However:
       * - For landscape (horizontal card) we set a height on a div equal to half the unscaled card height in pixels.  So
       *   the height we are showing (which is scaled) might be determined to be (480px * 0.324) = 155px but we set a height
       *   on the div of 240px (480px / 2).  We then use a transform (-50%) to position the image in the center of the div.
       *   BUT... because the container height is 85px (240-155px) taller than the image / it needs to be, we end up with
       *   a lot of extra white space.
       * - For portrait (vertical cards) there is a similar transform / scale issue in the horizontal direction.
       *
       * THIS PROP DISABLES THE TRANSFORM -50% / -50% and just scales the image to the right size.
       */
      disableTranslationScale: true;
    }
) &
  // Upsell specific props
  (| {
        isUpsellActive: true;
        stylesHand: string;
      }
    | {
        isUpsellActive?: false;
      }
  ) &
  // Animation props
  (| {
        animation: string | undefined;
        isGuestExperience?: boolean;
        replayAnimation?: boolean;
        setReplayAnimation?: (replayAnimation: boolean) => void;
      }
    | {
        animation?: never;
      }
  );

export const DigitalCanvas = (props: DigitalCanvasProps): JSX.Element | null => {
  const {
    isMobile,
    backgroundColor,
    elements,
    foilColor,
    opacity = 100,
    page,
    strongShadow = false,
    stylesCanvas,
    width,
    aspectRatioValue = 'initial',
    cardBackgroundColor,
    hideFoldedDisplay,
    shape,
    cardType,
    clipPathId,
    disableBleed = true,
    disableTranslationScale = false,
    alignItems = 'center',
    justifyContent = 'center',
  } = props;

  const [showReplayCTA, setShowReplayCTA] = useState(false);

  // settings to adjust the image container height
  // in case if the design is horizontally orientated
  const { width: pageWidthPts, height: pageHeightPts, folded, orientation, tags } = page;

  const bleed = useMemo(() => getCardBleed(cardType, page), [cardType, page]);

  const { cardHeightPts, cardWidthPts } = getCardDimensionsInPoints({
    pageWidthPts,
    pageHeightPts,
    isFoldable: folded,
    pageOrientation: orientation,
    cardType,
    bleed,
    disableBleed,
  });

  const cardHeightPx = ptToPx(cardHeightPts);
  const cardWidthPx = ptToPx(cardWidthPts);

  // provide the image size adjustment if the card is horizontally orientated
  const isLandscape = (orientation as unknown) === 'LANDSCAPE';

  const scale = useMemo(() => {
    if (!props.disableTranslationScale) {
      let calculatedScale = isLandscape
        ? props.imageWidth / cardWidthPx
        : width / Math.max(cardWidthPx, cardHeightPx);

      if (props.isXsMobile && props.windowWidth) {
        calculatedScale = props.windowWidth / (cardWidthPx + 45);
      }

      if (props.isUpsellActive) {
        calculatedScale = props.isMobile ? 0.39 : 0.715;
      }
      return calculatedScale;
    }
    return width / cardWidthPx;
  }, [cardHeightPx, cardWidthPx, isLandscape, props, width]);

  const transformScale = useMemo(() => `scale(${scale})`, [scale]);

  const {
    previewTransformRotate,
    previewTransformOrigin,
    previewHeight,
    previewWidth,
    previewMarginTop,
    previewMarginRight,
    cardPreviewMarginTop,
    cardPreviewMarginLeft,
    foldPreviewMarginTop,
    foldPreviewMarginLeft,
    foldPreviewTransformRotate,
    foldPreviewTransformOrigin,
    foldPreviewWidth,
    foldPreviewHeight,
  } = useGetPreviewStyling({
    pageHeight: cardHeightPx,
    pageWidth: cardWidthPx,
    isFoldable: folded,
    pageOrientation: orientation,
    scale,
    hideFoldedDisplay,
    disableTranslationScale,
  });

  const supportsNewShapeDisplay = cardType && isCardTypeWithAdditionalShapes(cardType);
  const shouldDisplayShapeClipPath =
    !!shape && supportsNewShapeDisplay && isCardShapeWithClipPath(shape);

  const dropShadow =
    shape === 'scalloped'
      ? 'drop-shadow(0 3px 1px rgba(0,0,0,0.1))'
      : 'drop-shadow(0 2px 6px rgba(0,0,0,0.1))';
  const borderRadius = shape === 'rounded' ? '52px' : '0px';
  const svgId = `svgClip_${clipPathId}`;

  const scalableContainerStyles: CSSProperties = useMemo(() => {
    let transform: CSSProperties =
      'isGuestExperience' in props && props.isGuestExperience && isMobile
        ? {
            left: '50%',
            transform: `translateX(-50%) ${transformScale}`,
            transformOrigin: 'center',
          }
        : {
            left: '50%',
            top: '50%',
            transform: `translate(-50%, -50%) ${transformScale}`,
            transformOrigin: 'center',
          };

    if (disableTranslationScale) {
      transform = {
        transform: transformScale,
        transformOrigin: 'top left',
      };
    }

    return {
      clipPath: shouldDisplayShapeClipPath ? `url(#${svgId})` : 'none',
      backgroundColor: cardBackgroundColor
        ? stringToHexCode(cardBackgroundColor)
        : COLORS3.WHITE_100,
      borderRadius,
      width: cardWidthPx,
      height: cardHeightPx,
      position: 'relative',
      boxSizing: 'content-box',
      overflow: 'hidden',
      boxShadow: strongShadow ? '0px 2px 6px rgba(0, 0, 0, 0.3)' : '0px 2px 6px rgba(0, 0, 0, 0.1)',
      ...transform,
    };
  }, [
    borderRadius,
    cardBackgroundColor,
    cardHeightPx,
    cardWidthPx,
    disableTranslationScale,
    isMobile,
    props,
    shouldDisplayShapeClipPath,
    strongShadow,
    svgId,
    transformScale,
  ]);

  const RenderedElements = useMemo(() => {
    const orderedElements = _sortBy(elements, (e) => e.layer);

    const boundingBoxesAdjusted = tags ? hasBoundingBoxAdjustedTag(tags) : false;
    const isPlaceCard = cardType === 'PLACE';

    return (
      <Fragment>
        {orderedElements.map((element) => (
          <CustomizationElement
            key={element.uuid}
            element={element}
            bleed={bleed}
            foilColor={foilColor}
            isPlaceCard={isPlaceCard}
            isBoundingBoxAdjusted={boundingBoxesAdjusted}
          />
        ))}
      </Fragment>
    );
  }, [foilColor, cardType, elements, tags, bleed]);

  return (
    <CanvasPreview
      className={cx(stylesCanvas, {
        isLandscape,
      })}
      backgroundColor={backgroundColor}
      opacity={opacity}
    >
      <div
        style={{
          height: props.isUpsellActive ? cardHeightPx * scale : '100%',
          position: 'relative',
          width: props.isUpsellActive ? cardWidthPx * scale : '',
          display: 'flex',
          alignItems,
          justifyContent,
          aspectRatio: aspectRatioValue,
        }}
        className="digital-canvas"
      >
        {props.isUpsellActive && <div className={cx(props.stylesHand)} />}
        <div
          style={{
            position: 'relative',
            width: previewWidth,
            // eslint-disable-next-line no-nested-ternary
            height: disableTranslationScale
              ? previewHeight
              : isLandscape
              ? previewHeight / 2
              : previewHeight,
            transform: hideFoldedDisplay ? previewTransformRotate : 'initial',
            transformOrigin: previewTransformOrigin,
            marginTop: previewMarginTop,
            marginRight: previewMarginRight,
            filter: dropShadow,
          }}
        >
          {folded && (
            <div
              id="fold-fold-fold"
              style={{
                backgroundColor: 'white',
                position: 'absolute',
                width: foldPreviewWidth,
                height: isLandscape ? foldPreviewHeight / 2 : foldPreviewHeight,
                marginTop: foldPreviewMarginTop,
                marginLeft: foldPreviewMarginLeft,
                transform: foldPreviewTransformRotate,
                transformOrigin: foldPreviewTransformOrigin,
                boxShadow: '0px 2px 6px rgba(0,  0,  0, .1)',
              }}
            />
          )}
          {props.animation && props.animation !== 'NONE' ? (
            <AnimatedCanvas
              animation={props.animation}
              height={cardHeightPx}
              isGuestExperience={
                'isGuestExperience' in props ? Boolean(props.isGuestExperience) : false
              }
              replayAnimation={props.replayAnimation}
              setReplayAnimation={props.setReplayAnimation}
              setShowReplayCTA={setShowReplayCTA}
              transformScale={transformScale}
              width={cardWidthPx}
            >
              {RenderedElements}
            </AnimatedCanvas>
          ) : (
            <div className="scalable-container" style={scalableContainerStyles}>
              {shouldDisplayShapeClipPath && (
                <svg width="0" height="0">
                  <clipPath id={svgId} clipPathUnits="objectBoundingBox">
                    <path
                      className={cx({
                        [styles.rotateSvg]: (orientation as unknown) === 'LANDSCAPE',
                      })}
                      d={CARD_SHAPE_SVG_PATHS[shape].clipPath}
                    />
                  </clipPath>
                </svg>
              )}

              <div
                style={{
                  marginTop: cardPreviewMarginTop,
                  marginLeft: cardPreviewMarginLeft,
                  position: 'relative',
                }}
              >
                {RenderedElements}
              </div>
            </div>
          )}
        </div>
        {props.animation && props.animation !== 'NONE' && props.isGuestExperience && (
          <div
            className={cx(styles.replayCta, {
              [styles.showReplayCta]: showReplayCTA,
            })}
          >
            <ButtonV3 onClick={() => props.setReplayAnimation?.(true)} variant="secondary">
              <RefreshLinkIcon style={{ marginRight: '4px' }} height={18} width={18} />
              Replay
            </ButtonV3>
          </div>
        )}
      </div>
    </CanvasPreview>
  );
};

/**
 * Adapts a customization within a card project to the DigitalCanvas component. This
 * finds primary customization and extracts the properties needed to render the
 * first page.
 *
 * This does not work with normalized data.  Only the raw data from the API.
 */
export const DigitalCanvasAdapter = ({
  cardProject,
  customization,
  ...rest
}: {
  cardProject: ConvertWebApiDeclaredToActual<WProjectFullView> | null;
  customization: ConvertWebApiDeclaredToActual<WProjectCustomizationFullView> | null;
} & Partial<DigitalCanvasProps>) => {
  if (!customization || !cardProject) {
    return null;
  }

  const primaryCustomization = findLeadCustomization(cardProject.customizations || []);
  if (!primaryCustomization) return null;

  const { type: primaryCustomizationType } = primaryCustomization;

  const { pages } = customization;
  const firstPage = pages?.find((p) => p.page_number === 1);
  if (!firstPage) return null;

  const firstPageElements = (firstPage.elements || []).filter(({ tag }) =>
    placeCardFilter(tag, customization)
  );

  // @ts-expect-error need to deal with ConvertWebApiDeclaredToActual
  const cardBackgroundColor = getSelectedColor(customization) || firstPage.background_color;

  const optionValues = customization.variation?.option_values || {};

  return (
    <DigitalCanvas
      // @ts-expect-error - we need to figure out if we're using ConvertWebApiDeclaredToActual or not and be consistent
      page={firstPage}
      // @ts-expect-error - we need to figure out if we're using ConvertWebApiDeclaredToActual or not and be consistent
      elements={firstPageElements}
      aspectRatioValue="1 / 1.1"
      cardBackgroundColor={cardBackgroundColor as string | undefined}
      hideFoldedDisplay={
        primaryCustomizationType === CARD_TYPE_MAP.thankyou &&
        (firstPage.orientation as unknown as string) === 'PORTRAIT'
      }
      // @ts-expect-error - words
      shape={isEnvelope(customization.type) ? 'square' : optionValues.silhouette}
      cardType={primaryCustomizationType as string}
      clipPathId={cardProject.uuid as string}
      foilColor={optionValues['foil-color']}
      {...rest}
    />
  );
};
